import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationPrefs } from '@lighthouse/shared';
import { useAuthStore } from '../hooks/useAuthStore';
import { useNotificationFeed } from '../hooks/useNotificationFeed';
import { api } from '../services/api';
import { enableWebPushNotifications } from '../services/notifications';

function BellIcon() {
  return (
    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
      />
    </svg>
  );
}

export function NotificationBell() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const { data: items = [], isLoading } = useNotificationFeed(!!user);
  const { data: prefs } = useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api.get<NotificationPrefs>('/auth/notification-prefs'),
    enabled: !!user,
  });

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  if (!user) return null;

  const pushOff = prefs?.pushEnabled === false;
  const count = items.length;

  const handleEnablePush = async () => {
    setEnabling(true);
    try {
      await enableWebPushNotifications();
      void qc.invalidateQueries({ queryKey: ['notification-prefs'] });
    } finally {
      setEnabling(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-full p-2 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <BellIcon />
        {count > 0 && (
          <span className="bg-primary-600 absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold text-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="glass-dropdown absolute right-0 z-50 mt-2 w-80 overflow-hidden">
          <div className="border-b border-white/60 px-4 py-3">
            <p className="text-sm font-bold text-gray-900">Notifications</p>
          </div>

          {pushOff && (
            <div className="bg-primary-50/80 border-b border-white/60 px-4 py-3">
              <p className="text-xs text-gray-700">
                Turn on push to get session reminders on this device.
              </p>
              <button
                type="button"
                onClick={() => void handleEnablePush()}
                disabled={enabling}
                className="btn-primary mt-2 w-full py-1.5 text-xs"
              >
                {enabling ? 'Enabling…' : 'Enable push notifications'}
              </button>
            </div>
          )}

          <div className="max-h-72 overflow-y-auto">
            {isLoading ? (
              <p className="px-4 py-6 text-center text-sm text-gray-500">Loading…</p>
            ) : items.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-gray-500">No upcoming reminders</p>
            ) : (
              items.map((item) => (
                <Link
                  key={item.id}
                  to={`/session/${item.bookingId}`}
                  onClick={() => setOpen(false)}
                  className="block border-b border-white/50 px-4 py-3 last:border-0 hover:bg-white/50"
                >
                  <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                  <p className="mt-0.5 text-xs text-gray-600">{item.body}</p>
                </Link>
              ))
            )}
          </div>

          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="text-primary-600 block border-t border-white/60 px-4 py-2.5 text-center text-xs font-semibold hover:bg-white/40"
          >
            Notification settings
          </Link>
        </div>
      )}
    </div>
  );
}
